import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { AUTH_API_ROOT, DRUPAL_SITE_URL, LoginAuth, LoginCredentials } from '../login/login.tokens';
import { ADMINISTRATOR_ROLE } from './roles';

interface LoginResponse {
  token: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService implements LoginAuth {
  private readonly tokenKey = 'authToken';

  constructor(
    private http: HttpClient,
    @Inject(AUTH_API_ROOT) private apiRoot: string,
    @Optional() @Inject(DRUPAL_SITE_URL) private drupalSiteUrl: string
  ) { }

  login(credentials: LoginCredentials): Observable<LoginResponse> {
    const url = `${this.apiRoot}auth/login`;
    return this.http.post<LoginResponse>(url, credentials).pipe(
      tap(response => {
        if (response && response.token) {
          localStorage.setItem(this.tokenKey, response.token);
        }
      }),
      catchError((err: HttpErrorResponse) => {
        const message = this.getLoginErrorMessage(err);
        return throwError(new Error(message));
      })
    );
  }

  logout(): boolean {
    localStorage.removeItem(this.tokenKey);
    if (!this.drupalSiteUrl) {
      return false;
    }

    try {
      this.assignLocation(this.buildLogoutRedirect());
      return true;
    } catch (e) {
      return false;
    }
  }

  private assignLocation(url: string): void {
    window.location.assign(url);
  }

  buildLogoutRedirect(): string {
    const url = new URL('/aacr/logout', this.ensureTrailingSlash(this.drupalSiteUrl));
    url.searchParams.set('return', this.loginPageUrl());
    return url.toString();
  }

  exchangeSso(code: string): Observable<LoginResponse> {
    const url = `${this.apiRoot}auth/sso`;
    return this.http.post<LoginResponse>(url, { code }).pipe(
      tap(response => {
        if (response && response.token) {
          localStorage.setItem(this.tokenKey, response.token);
        }
      }),
      catchError((err: HttpErrorResponse) => {
        const message = this.getLoginErrorMessage(err);
        return throwError(new Error(message));
      })
    );
  }

  beginSso(returnUrl: string): boolean {
    if (!this.drupalSiteUrl) {
      return false;
    }

    try {
      const state = this.createSsoState();
      const safeReturn = this.safeReturnUrl(returnUrl);
      sessionStorage.setItem('aacrSsoState', state);
      sessionStorage.setItem('aacrSsoReturnUrl', safeReturn);
      window.location.assign(this.buildSsoRedirect(state));
      return true;
    } catch (e) {
      return false;
    }
  }

  takeSsoReturnUrl(): string {
    const stored = sessionStorage.getItem('aacrSsoReturnUrl') || '/';
    sessionStorage.removeItem('aacrSsoReturnUrl');
    return this.safeReturnUrl(stored);
  }

  consumeSsoState(state: string | null): boolean {
    const expected = sessionStorage.getItem('aacrSsoState');
    sessionStorage.removeItem('aacrSsoState');
    return !!state && !!expected && state === expected;
  }

  buildSsoRedirect(state: string): string {
    const callback = this.callbackUrl();
    const url = new URL('/aacr/sso', this.ensureTrailingSlash(this.drupalSiteUrl));
    url.searchParams.set('return', callback);
    url.searchParams.set('state', state);
    return url.toString();
  }

  private createSsoState(): string {
    const bytes = new Uint8Array(16);
    if (window.crypto && window.crypto.getRandomValues) {
      window.crypto.getRandomValues(bytes);
    } else {
      for (let i = 0; i < bytes.length; i++) {
        bytes[i] = Math.floor(Math.random() * 256);
      }
    }
    return Array.from(bytes).map(byte => byte.toString(16).padStart(2, '0')).join('');
  }

  private callbackUrl(): string {
    return this.appPageUrl('sso/callback');
  }

  private loginPageUrl(): string {
    return this.appPageUrl('login');
  }

  private appPageUrl(page: string): string {
    const baseElement = document.querySelector('base');
    const baseHref = (baseElement && baseElement.getAttribute('href')) || '/';
    const path = baseHref.endsWith('/') ? `${baseHref}${page}` : `${baseHref}/${page}`;
    return new URL(path, window.location.origin).toString();
  }

  private safeReturnUrl(returnUrl: string): string {
    if (!returnUrl || returnUrl.indexOf('/') !== 0 || returnUrl.indexOf('//') === 0) {
      return '/';
    }
    return returnUrl;
  }

  private ensureTrailingSlash(value: string): string {
    return value.endsWith('/') ? value : `${value}/`;
  }

  private getLoginErrorMessage(err: HttpErrorResponse): string {
    if (err.status === 0) {
      return 'Unable to connect. Please check your network and try again.';
    }
    if (err.status === 401) {
      return 'Invalid username or password.';
    }
    if (err.status === 403) {
      return 'Access denied.';
    }
    if (err.status >= 500) {
      return 'The server is temporarily unavailable. Please try again later.';
    }
    const message = err.error?.message ?? err.message ?? 'Login failed. Please try again.';
    return typeof message === 'string' ? message : 'Login failed. Please try again.';
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getRoles(): string[] {
    const payload = this.getTokenPayload();
    if (!payload) {
      return [];
    }

    const roleClaim = payload['role']
      || payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];

    return this.parseRoleClaim(roleClaim);
  }

  hasAnyRole(allowed: string[]): boolean {
    if (!allowed || allowed.length === 0) {
      return false;
    }

    const roles = this.getRoles().map(role => String(role).toLowerCase());
    return allowed.some(role => roles.indexOf(String(role).toLowerCase()) !== -1);
  }

  isAdmin(): boolean {
    return this.hasAnyRole([ADMINISTRATOR_ROLE]);
  }

  private getTokenPayload(): { [key: string]: unknown } | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    try {
      const parts = token.split('.');
      if (parts.length < 2 || !parts[1]) {
        return null;
      }

      const base64 = parts[1].replace(/-/g, '+').replace(/\//g, '_');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      return JSON.parse(atob(padded));
    } catch {
      return null;
    }
  }

  private parseRoleClaim(roleClaim: unknown): string[] {
    if (roleClaim == null) {
      return [];
    }

    let roles: unknown[];
    if (Array.isArray(roleClaim)) {
      roles = roleClaim;
    } else if (typeof roleClaim === 'string') {
      const trimmed = roleClaim.trim();
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          roles = Array.isArray(parsed) ? parsed : [parsed];
        } catch {
          roles = [trimmed];
        }
      } else {
        roles = [trimmed];
      }
    } else {
      return [];
    }

    return roles
      .map(role => String(role).trim())
      .filter(role => role.length > 0);
  }
}
